import { useState } from "react";
import CardBrandIcon from "./CardBrandIcon";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CreditCard, Loader2, Plus, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import AddCardDialog from "./AddCardDialog";

export type PaymentMethod = {
  id: string;
  brand?: string;
  last4?: string;
  exp_month?: number;
  exp_year?: number;
  type?: string;
};

interface Props {
  paymentMethods: PaymentMethod[];
  defaultPmId: string | null;
  hasActiveSubscription: boolean;
  onChanged: () => void;
}

const PaymentMethodsList = ({ paymentMethods, defaultPmId, hasActiveSubscription, onChanged }: Props) => {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const callAction = async (action: string, payment_method_id: string) => {
    setBusyId(payment_method_id);
    try {
      const { data, error } = await supabase.functions.invoke("manage-billing", {
        body: { action, payment_method_id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(action === "set_default_pm" ? "Default card updated" : "Card removed");
      onChanged();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>Payment methods</CardTitle>
          <CardDescription>Manage cards used for your subscription</CardDescription>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Add card
        </Button>
      </CardHeader>
      <CardContent>
        {paymentMethods.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {hasActiveSubscription
              ? "Stripe has an active subscription, but no saved card is attached yet. Add a card to use for renewals."
              : "No cards on file."}
          </p>
        ) : (
          <ul className="divide-y">
            {paymentMethods.map((pm) => {
              const isDefault = pm.id === defaultPmId;
              const busy = busyId === pm.id;
              const isLink = pm.type === "link" || pm.brand === "link";
              const displayBrand = isLink ? "Stripe Link" : pm.brand ?? "Card";
              return (
                <li key={pm.id} className="py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-4">
                    <CardBrandIcon brand={pm.brand} className="h-10 w-16 shrink-0" />
                    <div>
                      <div className="font-semibold flex items-center gap-2">
                        <span className="uppercase tracking-wide">{displayBrand}</span>
                        {isLink ? (
                          <span className="font-mono text-muted-foreground">saved checkout payment</span>
                        ) : pm.last4 ? (
                          <span className="font-mono text-muted-foreground">
                            •••• <span className="text-foreground font-semibold">{pm.last4}</span>
                          </span>
                        ) : (
                          <span className="font-mono text-muted-foreground">•••• ••••</span>
                        )}
                        {isDefault && <Badge variant="secondary">Default</Badge>}
                      </div>
                      {!isLink && (
                        <div className="text-xs text-muted-foreground">
                          Expires {String(pm.exp_month ?? "").padStart(2, "0")}/{pm.exp_year ?? ""}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isDefault && (
                      <Button
                        size="sm" variant="outline" disabled={busy}
                        onClick={() => callAction("set_default_pm", pm.id)}
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Star className="h-4 w-4 mr-1" />Set default</>)}
                      </Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" disabled={busy}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remove this card?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {paymentMethods.length === 1 && hasActiveSubscription
                              ? "This is your only card. Add another card before removing this one."
                              : "Stripe will detach this card from your account."}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => callAction("delete_pm", pm.id)}
                            disabled={paymentMethods.length === 1 && hasActiveSubscription}
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      <AddCardDialog open={addOpen} onOpenChange={setAddOpen} onAdded={onChanged} />
    </Card>
  );
};

export default PaymentMethodsList;
